'use strict';
const options_collection = {
  collectionsLoadMore: '.collections-load-more',
  rangeSlider: '.price-range .range-slider',
  rangeSliderMin: '.range-slider-min',
  rangeSliderMax: '.range-slider-max',
  minPriceRange: '.min-price-range',
  maxPriceRange: '.max-price-range',
  collectionSidebar: '#CollectionSidebar',
  collectionFilterBlocks: '.filter-blocks',
  productgridcontainer: '#productgridcontainer',
  collectionFiltersForm: '#CollectionFiltersForm',
  section: '.sec__collection-main',
};

// VoiceOver/WebKit can retain the popup navigation context unless focus is
// restored after the sort panel has finished closing.
function a11yReturnFocusToSortTrigger(sorter) {
  if (!sorter || typeof sorter.focus !== 'function') return;
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      try {
        sorter.focus({ preventScroll: true });
      } catch (error) {
        sorter.focus();
      }
    });
  });
}

const filterDesktopMedia = window.matchMedia('(min-width: 1025px)');
let filterViewportListenerBound = false;

function isFilterSidebarModal(sidebar) {
  return Boolean(sidebar) && (sidebar.classList.contains('drawer') || !filterDesktopMedia.matches);
}

function syncFilterSidebarA11y(sidebar) {
  if (!sidebar) return;
  if (!isFilterSidebarModal(sidebar)) {
    sidebar.removeAttribute('role');
    sidebar.removeAttribute('aria-modal');
    sidebar.removeAttribute('aria-labelledby');
    sidebar.removeAttribute('aria-hidden');
    sidebar.removeAttribute('inert');
    return;
  }

  const heading = sidebar.querySelector('.sidebar-header [id]');
  sidebar.setAttribute('role', 'dialog');
  sidebar.setAttribute('aria-modal', 'true');
  if (heading?.id) sidebar.setAttribute('aria-labelledby', heading.id);

  if (sidebar.classList.contains('open')) {
    sidebar.removeAttribute('aria-hidden');
    sidebar.removeAttribute('inert');
  } else {
    sidebar.setAttribute('aria-hidden', 'true');
    sidebar.setAttribute('inert', '');
  }
}

function removeFilterDrawerHandlers(button, fallbackSidebar) {
  if (button.handleEsc) {
    document.removeEventListener('keydown', button.handleEsc);
    button.handleEsc = null;
  }

  const listenerSidebar = button.filterFocusTrapSidebar || button.collectionSidebar || fallbackSidebar;
  if (button.handleFocusTrap) {
    if (listenerSidebar) listenerSidebar.removeEventListener('keydown', button.handleFocusTrap);
    button.handleFocusTrap = null;
  }
  button.filterFocusTrapSidebar = null;
}

function clearFilterDrawerInteraction(sidebar, returnFocus = false) {
  const btnFilters = Array.from(document.querySelectorAll('.btn-filter'));
  const openedFilterBtn = btnFilters.find((button) => button.classList.contains('open'));

  if (sidebar) sidebar.classList.remove('open');
  document.documentElement.classList.remove('open-drawer', 'open-sidebar');
  root.style.removeProperty('padding-right');
  btnFilters.forEach((button) => {
    button.classList.remove('open');
    button.setAttribute('aria-expanded', 'false');
    removeFilterDrawerHandlers(button, sidebar);
  });

  syncFilterSidebarA11y(sidebar);

  if (returnFocus) {
    const focusTarget = openedFilterBtn && openedFilterBtn.offsetParent !== null
      ? openedFilterBtn
      : btnFilters.find((button) => button.offsetParent !== null);
    if (focusTarget) {
      try {
        focusTarget.focus({ preventScroll: true });
      } catch (error) {
        focusTarget.focus();
      }
    }
  }
}

function syncFilterSidebarForViewport(sidebar) {
  if (!sidebar) return;
  if (!isFilterSidebarModal(sidebar) && sidebar.classList.contains('open')) {
    clearFilterDrawerInteraction(sidebar);
  }
  syncFilterSidebarA11y(sidebar);
}

function initializeFilterSidebarA11y(sidebar) {
  syncFilterSidebarForViewport(sidebar);
  if (filterViewportListenerBound) return;
  const onViewportChange = () => {
    syncFilterSidebarForViewport(document.getElementById('CollectionSidebar'));
  };
  if (typeof filterDesktopMedia.addEventListener === 'function') {
    filterDesktopMedia.addEventListener('change', onViewportChange);
  } else {
    filterDesktopMedia.addListener(onViewportChange);
  }
  filterViewportListenerBound = true;
}

class BtnFilter extends HTMLElement {
  constructor() {
    super();
    this.collectionSidebar = document.getElementById('CollectionSidebar');
    initializeFilterSidebarA11y(this.collectionSidebar);
    this.addEventListener('click', this.onOpen.bind(this), false);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.onOpen();
      }
    });
  }
  onOpen() {
    this.collectionSidebar = document.getElementById('CollectionSidebar');
    if (!this.collectionSidebar) return;
    if (!this.classList.contains('open')) {
      removeFilterDrawerHandlers(this, this.collectionSidebar);
      this.collectionSidebar.removeAttribute('aria-hidden');
      this.collectionSidebar.removeAttribute('inert');
      this.collectionSidebar.classList.add('open');
      syncFilterSidebarA11y(this.collectionSidebar);
      document.documentElement.classList.add('open-drawer', 'open-sidebar');
      root.style.setProperty('padding-right', getScrollBarWidth.init() + 'px');
      this.classList.add('open');
      this.setAttribute('aria-expanded', 'true');

      const closeBtn = this.collectionSidebar.querySelector('close-filter');
      if (closeBtn) {
        const openedSidebar = this.collectionSidebar;
        setTimeout(() => {
          if (openedSidebar.classList.contains('open')) closeBtn.focus();
        }, 100);
      }

      const trappedSidebar = this.collectionSidebar;
      this.handleEsc = (e) => {
        if (e.key === 'Escape') {
          const closeFilter = trappedSidebar.querySelector('close-filter');
          if (closeFilter) closeFilter.click();
        }
      };

      this.handleFocusTrap = (e) => {
        if (e.key !== 'Tab') return;

        const focusableElements = Array.from(trappedSidebar.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter((element) => {
          return !element.disabled &&
            !element.closest('[hidden], [inert], [aria-hidden="true"]') &&
            element.getClientRects().length > 0;
        });
        if (!focusableElements.length) return;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) { /* shift + tab */
          if (document.activeElement === firstElement) {
            lastElement.focus();
            e.preventDefault();
          }
        } else { /* tab */
          if (document.activeElement === lastElement) {
            firstElement.focus();
            e.preventDefault();
          }
        }
      };

      document.addEventListener('keydown', this.handleEsc);
      trappedSidebar.addEventListener('keydown', this.handleFocusTrap);
      this.filterFocusTrapSidebar = trappedSidebar;
    } else {
      clearFilterDrawerInteraction(this.collectionSidebar);
    }
  }
}
customElements.define('btn-filter', BtnFilter);
class CloseFilter extends HTMLElement {
  constructor() {
    super();
    this.collectionSidebar = document.getElementById('CollectionSidebar');
    initializeFilterSidebarA11y(this.collectionSidebar);
    this.addEventListener('click', this.onClose.bind(this), false);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.onClose();
      }
    });
  }
  onClose() {
    this.collectionSidebar = document.getElementById('CollectionSidebar');
    if (!this.collectionSidebar) return;
    clearFilterDrawerInteraction(this.collectionSidebar, true);
  }
}
customElements.define('close-filter', CloseFilter);

class FacetApplyCanvas extends CloseFilter {}
customElements.define('facet-apply-canvas', FacetApplyCanvas);

class GridMode extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.gridMode.bind(this), false);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.gridMode();
      }
    });
  }
  async gridMode() {
    if (this.classList.contains('active')) return;
    const view_mode = this.getAttribute('data-grid-mode');
    if (view_mode == 1) return;
  await motion.animate(
      document.querySelectorAll(
        'motion-element.product-item__wrapper.slide_in'
      ),
      {
        transform: ['translateY(0)', 'translateY(3.5rem)'],
        opacity: [1, 0.01],
        visibility: ['visible', 'hidden'],
      },
      {
        duration: 0.3,
        delay: motion.stagger(0.05),
        easing: [0, 0, 0.3, 1],
      }
    );


    for (var item of document.querySelectorAll('grid-mode, view-mode')) {
      item.classList.remove('active');
      item.setAttribute('aria-pressed', 'false');
    }
    const container_switch = document.querySelector(
      '.container-products-switch'
    );
    this.classList.add('active');
    this.setAttribute('aria-pressed', 'true');
    const data_view_mode = container_switch.getAttribute('data-view-mode');
    container_switch.classList.remove('grid-columns-' + data_view_mode);
    container_switch.setAttribute('data-view-mode', view_mode);
    container_switch.classList.add('grid-columns-' + view_mode);
    motion.animate(
      document.querySelectorAll(
        'motion-element.product-item__wrapper.slide_in'
      ),
      {
        transform: ['translateY(3.5rem)', 'translateY(0)'],
        opacity: [0.01, 1],
        visibility: ['hidden', 'visible'],
      },
      {
        duration: 0.3,
        delay: motion.stagger(0.1),
        easing: [0, 0, 0.3, 1],
      }
    );
    let queryString = window.location.search;
    queryString = eventCollectionShopify.removeParam('column', queryString);
    const searchParams = queryString.replace('?', '') + '&column=' + view_mode;
    const urlUpdate = `${window.location.pathname}?${searchParams}`;
    window.history.replaceState({ url: urlUpdate }, '', urlUpdate);
    eventCollectionShopify.applyColumnToLinks(view_mode);
  }
}
customElements.define('grid-mode', GridMode);

class ViewMode extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.viewMode.bind(this), false);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.viewMode();
      }
    });
  }
  viewMode() {
    const view_mode = this.getAttribute('data-view');
    if (this.getAttribute('aria-pressed') === 'true') return;
    document.querySelectorAll('grid-mode, view-mode').forEach((item) => {
      item.classList.remove('active');
      item.setAttribute('aria-pressed', 'false');
    });
    this.classList.add('active');
    this.setAttribute('aria-pressed', 'true');
    var queryString = window.location.search;
    queryString = eventCollectionShopify.removeParam('view', queryString);
    if (view_mode == 'list') {
      var searchParams = queryString.replace('?', '') + '&view=list';
    } else {
      var searchParams = queryString.replace('?', '') + '&view=grid';
    }
    const url = eventCollectionShopify.renderUrl(searchParams);
    eventCollectionShopify.renderSectionFilter(url, searchParams);
  }
}
customElements.define('view-mode', ViewMode);

class FilterItem extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.filterItem.bind(this), false);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });
  }
  filterItem() {
    const swatch = this.querySelector('.swatch-option');
    if (swatch) {
      swatch.classList.toggle('current-filter');
    } else if (this.querySelector('.tooltip')) {
      this.querySelector('.tooltip').classList.toggle('current-filter');
    } else {
      if (this.closest('li')) {
        this.closest('li').classList.toggle('current-filter');
      }
    }
    const url = this.getAttribute('data-href');
    eventCollectionShopify.renderSectionFilter(url);
  }
}
customElements.define('filter-item', FilterItem);

class FacetClearAll extends CloseFilter {
  constructor() {
    super();
    this.addEventListener('click', this.filterItem.bind(this), false);
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.filterItem();
      }
    });
  }
  filterItem() {
    const url = this.getAttribute('data-href');
    eventCollectionShopify.renderSectionFilter(url);
    this.onClose();
  }
}
customElements.define('facet-clear-all', FacetClearAll);

class FilterSort extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.selectOption.bind(this), false);
    this.addEventListener('keydown', this.handleKeydown.bind(this));
  }

  handleKeydown(e) {
    const group = this.closest('[role="radiogroup"]');
    if (!group) return;
    const items = Array.from(group.querySelectorAll('filter-sort'));
    const index = items.indexOf(this);

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.selectOption();
        break;
      case 'ArrowDown':
        e.preventDefault();
        items[(index + 1) % items.length]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        items[(index - 1 + items.length) % items.length]?.focus();
        break;
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Escape': {
        e.preventDefault();
        e.stopPropagation();
        const sorter = this.closest('.select-custom')?.querySelector('select-sorter');
        if (sorter && typeof sorter.close === 'function') {
          sorter.close();
          a11yReturnFocusToSortTrigger(sorter);
        }
        break;
      }
      case 'Tab': {
        const sorter = this.closest('.select-custom')?.querySelector('select-sorter');
        if (sorter) sorter.close();
        break;
      }
    }
  }

  selectOption() {
    const group = this.closest('[role="radiogroup"]');
    if (group) {
      group.querySelectorAll('filter-sort').forEach((option) => {
        option.setAttribute('aria-checked', 'false');
      });
    }
    this.setAttribute('aria-checked', 'true');

    const value = this.getAttribute('value');
    let queryString = window.location.search;
    queryString = eventCollectionShopify.removeParam('sort_by', queryString);
    const searchParams = queryString.replace('?', '') + '&sort_by=' + value;
    const url = eventCollectionShopify.renderUrl(searchParams);
    eventCollectionShopify.renderSectionFilter(url, searchParams);
  }
}
customElements.define('filter-sort', FilterSort);

class SelectSorter extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.toggle.bind(this), false);
    this.addEventListener('keydown', this.handleKeydown.bind(this));
    this._handleOutsideClick = this.handleOutsideClick.bind(this);
    this._handleEsc = this.handleEsc.bind(this);
  }

  getPanel() {
    const id = this.getAttribute('aria-controls');
    if (id) {
      const panel = document.getElementById(id);
      if (panel) return panel;
    }
    return this.closest('.select-custom')?.querySelector('.select-custom__content') ?? null;
  }

  handleKeydown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggle();
        break;
      case 'Escape':
        if (this.getAttribute('aria-expanded') === 'true') {
          e.preventDefault();
          e.stopPropagation();
          this.close();
          a11yReturnFocusToSortTrigger(this);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (this.getAttribute('aria-expanded') !== 'true') this.open();
        this.focusOption('first');
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this.getAttribute('aria-expanded') !== 'true') this.open();
        this.focusOption('last');
        break;
    }
  }

  toggle() {
    const container = this.closest('.select-custom');
    if (container.classList.contains('active')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    const container = this.closest('.select-custom');
    const panel = this.getPanel();
    if (panel) {
      panel.removeAttribute('inert');
      panel.setAttribute('aria-hidden', 'false');
    }
    container.classList.add('active');
    this.setAttribute('aria-expanded', 'true');
    // Defer adding listeners to prevent immediate triggering
    setTimeout(() => {
      document.addEventListener('click', this._handleOutsideClick);
      document.addEventListener('keydown', this._handleEsc);
      this._handleFocusOut = this.handleFocusOut.bind(this);
      container.addEventListener('focusout', this._handleFocusOut);
    }, 0);
  }

  close() {
    const container = this.closest('.select-custom');
    const panel = this.getPanel();
    if (panel) {
      const activeElement = document.activeElement;
      if (activeElement && panel.contains(activeElement) && typeof activeElement.blur === 'function') {
        activeElement.blur();
      }
      panel.setAttribute('aria-hidden', 'true');
      panel.setAttribute('inert', '');
    }
    if (container) {
      container.classList.remove('active');
      if (this._handleFocusOut) container.removeEventListener('focusout', this._handleFocusOut);
    }
    this.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this._handleOutsideClick);
    document.removeEventListener('keydown', this._handleEsc);
  }

  focusOption(which) {
    const container = this.closest('.select-custom');
    if (!container) return;
    const options = container.querySelectorAll('filter-sort');
    if (!options.length) return;

    let target;
    if (which === 'first') target = options[0];
    if (which === 'last') target = options[options.length - 1];
    if (target) target.focus();
  }

  handleOutsideClick(event) {
    const container = this.closest('.select-custom');
    if (container && !container.contains(event.target)) {
      this.close();
    }
  }

  handleEsc(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.close();
      a11yReturnFocusToSortTrigger(this);
    }
  }

  handleFocusOut(event) {
    const container = this.closest('.select-custom');
    setTimeout(() => {
      if (container && !container.contains(document.activeElement)) {
        this.close();
      }
    });
  }
}
customElements.define('select-sorter', SelectSorter);

var eventCollectionShopify = (function () {
  function isV2CollectionPage() {
    return !!document.querySelector('.sa-collection-main--product-grid');
  }

  function captureSortFocusState() {
    const activeSortOption = document.activeElement?.closest?.('filter-sort');
    if (!activeSortOption) return null;

    const section = activeSortOption.closest(options_collection.section);
    const sorter = activeSortOption.closest('.select-custom')?.querySelector('select-sorter');
    if (!section || !sorter) return null;

    const sectionSorters = Array.from(section.querySelectorAll('select-sorter'));
    return {
      activeSortOption,
      sectionId: section.dataset.sectionId || '',
      controls: sorter.getAttribute('aria-controls') || '',
      sorterIndex: sectionSorters.indexOf(sorter),
    };
  }

  function restoreSortFocus(state) {
    // If a render path leaves the toolbar connected, its focused option is
    // still valid and should keep the existing interaction state.
    if (!state || state.activeSortOption.isConnected) return;

    const sections = Array.from(document.querySelectorAll(options_collection.section));
    const section = sections.find((item) => {
      return !state.sectionId || item.dataset.sectionId === state.sectionId;
    });
    if (!section) return;

    const sorters = Array.from(section.querySelectorAll('select-sorter'));
    const sorter = sorters.find((item) => {
      return state.controls && item.getAttribute('aria-controls') === state.controls;
    }) || sorters[state.sorterIndex] || sorters[0];

    a11yReturnFocusToSortTrigger(sorter);
  }

  return {
    init: function () {
      if (isV2CollectionPage()) return;
      this.ionRangeSlider();
      this.applyColumnFromUrl();
      let windowWidth = window.innerWidth;
      if (windowWidth <= 1024) {
        const horizontal = document.querySelector('.horizontal-filter');
        if (horizontal) {
          const collapsible = document.querySelectorAll('collapsible-block');
          collapsible.forEach((e) => {
            e.classList.add('active');
          });
        }
      }
    },

    ionRangeSlider: function () {
      const price_range = document.querySelector('.price-range');
      const maxRange = document.querySelector(
        options_collection.rangeSliderMax
      );
      const minRange = document.querySelector(
        options_collection.rangeSliderMin
      );
      const minPrice = document.querySelector('.min-price-range');
      const maxPrice = document.querySelector('.max-price-range');
      var minPriceRange = 0;
      var maxPriceRange = 0;
      if (!price_range) return;
      const submitPriceFilter = () => {
        const form = document.querySelector(options_collection.collectionFiltersForm);
        if (!form) return;
        const formData = new FormData(form);
        const params = new URLSearchParams(formData).toString();
        const nameMin = minPrice.getAttribute('name');
        const nameMax = maxPrice.getAttribute('name');
        let queryString = window.location.search;
        queryString = this.removeParam(nameMin, queryString);
        queryString = this.removeParam(nameMax, queryString);
        const searchParams = queryString.replace('?', '') + '&' + params;
        const url = this.renderUrl(searchParams);
        this.renderSectionFilter(url, searchParams);
      };
      let priceFilterTimer;
      const schedulePriceFilter = () => {
        clearTimeout(priceFilterTimer);
        priceFilterTimer = setTimeout(submitPriceFilter, 150);
      };

      document.querySelector(options_collection.minPriceRange).addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          const target = event.currentTarget;
          target.value = target.value.replace(',', '');
          if (Number(minRange.min) + Number(target.value) >= 0) {
            if (Number(target.value) > Number(maxRange.max)) {
              minPrice.value = Number(maxRange.max);
            } else {
              minPrice.value = Number(target.value);
            }
          } else {
            minPrice.value = Number(minRange.min);
          }
          schedulePriceFilter();
        },
        false
      );
      document.querySelector(options_collection.maxPriceRange).addEventListener(
        'change',
        (event) => {
          event.preventDefault();
          const target = event.currentTarget;
          target.value = target.value.replace(',', '');
          if (Number(maxRange.max) - Number(target.value) >= 0) {
            maxPrice.value = Number(target.value);
          } else {
            maxPrice.value = Number(maxRange.max);
          }
          schedulePriceFilter();
        },
        false
      );
      document
        .querySelector(options_collection.rangeSliderMin)
        .addEventListener(
          'input',
          (event) => {
            event.preventDefault();
            const target = event.currentTarget;
            if (Number(maxRange.value) - Number(target.value) >= 1) {
              minPrice.value = Number(target.value);
              minPriceRange = Number(target.value) * 100;
            } else {
              target.value = Number(maxRange.value) - 1;
              minPriceRange = (Number(maxRange.value) - 1) * 100;
            }
            const price_format = Shopify.formatMoney(
              minPriceRange,
              cartStrings.money_format
            );
            document.querySelector('.price-lable .min').innerHTML =
              price_format;
            target
              .closest('.price-range')
              .style.setProperty(
                '--range-from',
                (minRange.value / Number(target.getAttribute('max'))) * 100 +
                  '%'
              );
          },
          false
        );
      document
        .querySelector(options_collection.rangeSliderMax)
        .addEventListener(
          'input',
          (event) => {
            event.preventDefault();
            const target = event.currentTarget;
            if (target.value - minRange.value >= 1) {
              maxPrice.value = target.value;
              maxPriceRange = Number(target.value) * 100;
            } else {
              target.value = Number(minRange.value) + Number(1);
              maxPriceRange = (Number(minRange.value) + Number(1)) * 100;
            }
            const price_format = Shopify.formatMoney(
              maxPriceRange,
              cartStrings.money_format
            );
            document.querySelector('.price-lable .max').innerHTML =
              price_format;
            target
              .closest('.price-range')
              .style.setProperty(
                '--range-to',
                (maxRange.value / Number(target.getAttribute('max'))) * 100 +
                  '%'
              );
          },
          false
        );
      document.querySelectorAll(options_collection.rangeSlider).forEach((sliderRange) => {
        sliderRange.addEventListener('change', submitPriceFilter, false);
      });
    },

    removeParam: function (key, sourceURL) {
      var rtn = sourceURL.split('?')[0],
        param,
        params_arr = [],
        queryString =
          sourceURL.indexOf('?') !== -1 ? sourceURL.split('?')[1] : '';
      if (queryString !== '') {
        params_arr = queryString.split('&');
        for (var i = params_arr.length - 1; i >= 0; i -= 1) {
          param = params_arr[i].split('=')[0];
          if (param === key) {
            params_arr.splice(i, 1);
          }
        }
        if (params_arr.length) rtn = rtn + '?' + params_arr.join('&');
      }
      return rtn;
    },

    renderUrl: function (searchParams) {
      const sectionId = document.querySelector(options_collection.section)
        .dataset.sectionId;
      const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;
      return url;
    },

    // pagination / load-more links are server-rendered <a href> without the
    // client-chosen column; rewrite them so a full-page load keeps the columns
    applyColumnToLinks: function (column) {
      document
        .querySelectorAll('.pagination__list a[href], .actions-loadmore[href]')
        .forEach((link) => {
          const url = new URL(link.getAttribute('href'), window.location.origin);
          if (url.protocol !== 'http:' && url.protocol !== 'https:') return;
          url.searchParams.set('column', column);
          link.setAttribute('href', url.pathname + url.search + url.hash);
        });
    },

    // pagination links are re-created by the server on every full-page load and
    // by renderSectionFilter's AJAX re-render, always without `column`. Re-apply
    // it from the URL so the chosen column survives every navigation, not just
    // the first hop after clicking a column button.
    applyColumnFromUrl: function () {
      const column = new URLSearchParams(window.location.search).get('column');
      if (column === '2' || column === '3' || column === '4') {
        this.applyColumnToLinks(column);
      }
    },

    renderSectionFilter: function (url, searchParams) {
      if (isV2CollectionPage()) return;
      const sortFocusState = captureSortFocusState();
      const column = new URLSearchParams(window.location.search).get('column');
      if (column && url.indexOf('column=') === -1) {
        url = url + (url.indexOf('?') === -1 ? '?' : '&') + 'column=' + column;
      }
      if (column && searchParams && searchParams.indexOf('column=') === -1) {
        searchParams = searchParams + '&column=' + column;
      }
      this.toggleLoading(document.body, true);
      const grid_list = document.querySelector('grid-list')
      grid_list.hideGridItems();
      fetch(`${url}`)
        .then((response) => {
          if (!response.ok) {
            var error = new Error(response.status);
            throw error;
          }
          return response.text();
        })
        .then((responseText) => {
          const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
          const currentSidebar = document.querySelector(options_collection.collectionSidebar);
          const shouldReturnFilterFocus = Boolean(
            currentSidebar?.classList.contains('open') || document.querySelector('.btn-filter.open')
          );
          clearFilterDrawerInteraction(currentSidebar, shouldReturnFilterFocus);

          if (window.innerWidth <= 1024) {
            const collectionFilterBlocks = parsedDoc
              .querySelector(options_collection.collectionFilterBlocks);
            document.querySelector(
              options_collection.collectionFilterBlocks
            ).innerHTML = collectionFilterBlocks.innerHTML;
            const productgridcontainer = parsedDoc
              .querySelector(options_collection.productgridcontainer);
            document.querySelector(
              options_collection.productgridcontainer
            ).innerHTML = productgridcontainer.innerHTML;
            clearFilterDrawerInteraction(
              document.querySelector(options_collection.collectionSidebar),
              shouldReturnFilterFocus
            );
          } else {
            const newSection = parsedDoc.querySelector(options_collection.section);
            document
              .querySelector(options_collection.section)
              .replaceWith(newSection);
            clearFilterDrawerInteraction(
              document.querySelector(options_collection.collectionSidebar),
              shouldReturnFilterFocus
            );
          }
          restoreSortFocus(sortFocusState);
          document.documentElement.classList.remove('hside_opened', 'open-drawer', 'open-sidebar');
          // [DTC-840] 筛选/排序 AJAX 重渲染的卡片错过 DOMContentLoaded 一次性精修，重新精修（真实 IP 结果有 promise 缓存，不重复请求）
          if (typeof refineRegionByRealIP === 'function') refineRegionByRealIP();
          const sectionElement = document.querySelector(
            '#productgridcontainer'
          );
          
          if (sectionElement) {
            const elementPosition = sectionElement.getBoundingClientRect().top;
            const offsetPosition = window.pageYOffset;
            const extraOffset = 100;
            const scrollToPosition =
              elementPosition + offsetPosition - extraOffset;
            window.scrollTo({
              top: scrollToPosition,
              behavior: 'smooth',
            });
          }
          this.toggleLoading(document.body, false);

          const liveRegion = document.querySelector('#search-results-live-region');
          if (liveRegion) {
            const query = new URLSearchParams(window.location.search).get('q') || '';
            const count = document.querySelectorAll('#product-grid .grid__item').length;
            const template = count === 0
              ? liveRegion.dataset.none
              : count === 1
                ? liveRegion.dataset.one
                : liveRegion.dataset.other;
            if (template) {
              liveRegion.textContent = template
                .replace('__COUNT__', String(count))
                .replace('__TERMS__', query);
            }
          }
          
        })
        .catch((error) => {
          this.toggleLoading(document.body, false);
          throw error;
        })
        .finally(() => {
          grid_list?.showGridItems();
          initLazyloadItem();
          BlsLazyloadImg.init();
          eventCollectionShopify.ionRangeSlider();
          eventCollectionShopify.applyColumnFromUrl();
        });
      this.updateUrl(url, searchParams);
    },

    toggleLoading: function (event, loading) {
      if (event) {
        if (loading) {
          event.classList.add('start', 'loading');
        } else {
          event.classList.add('finish');
          setTimeout(function () {
            event.classList.remove('start', 'loading', 'finish');
          }, 500);
        }
      }
    },

    updateUrl: function (url, searchParams = false) {
      if (searchParams) {
        const urlUpdate = `${window.location.pathname}?${searchParams}`;
        window.history.pushState({ url: urlUpdate }, '', urlUpdate);
      } else {
        window.history.pushState({ url: url }, '', url);
      }
    },
  };
})();
eventCollectionShopify.init();
